import { isEmpty, isNil, omit, uniq } from 'lodash'

import config from '@acre/config'

import { clearCaseFromCache, fetchCase, fetchCasesForClient, updateCase } from '../../api/case'
import {
  addClient,
  createClientOrganisationRelationship,
  fetchClient,
  fetchClientByVersion,
  fetchClientOrganisationRelationships,
  fetchClients,
  fetchClientsWithPagination,
  fetchClientVerifications,
  fetchProtectedClientFields,
  getClientByVersion,
  requestEidvCheck,
  updateClient,
  updateClientAddresses,
  updateClientOrganisationRelationship,
  upsertCompany,
  upsertCompanyAndDirectors,
} from '../../api/client'
import { getMiDocumentSummaries, getMiDocumentTotals } from '../../api/document'
import { fetchMortgages, updateMortgage } from '../../api/mortgage'
import {
  ClientPropertyLoader,
  fetchClientProperties,
  fetchPropertiesForClients,
  updateProperty,
} from '../../api/property'
import { deleteCaseProtectionProduct, fetchAllProtectionProducts } from '../../api/protection_product'
import { createMandate, getNeedsMandate, getPayments } from '../../api_client_portal/accounting'
import {
  ClientLoaderUser,
  fetchClientsBatchFunction,
  fetchClientUser,
  fetchProtectedClientUserFields,
  getClientUserDocumentDetails,
  updateClientUser,
} from '../../api_client_portal/client_user'
import { getDocumentVerificationCp } from '../../api_client_portal/document'
import { ClientPropertyLoaderCp } from '../../api_client_portal/property'
import { getRiskReport } from '../../api_protection/methods'
import type { Resolvers } from '../../generated/server/resolvers'
import type { CdmClient, CdmGetClientRequest, CdmGetProtectionProductRequest } from '../../service/luther/model'
import { isNonNaturalClient, separateRelationships } from '../../utils/clients/clients'
import { getDirectors } from '../../utils/getDirectors'
import { formatClientAsRequestBody, formatClientUser, formatNationalities } from '../../utils/schemaMapping'
import { GraphqlException } from '../util'
import {
  addClientAndUpdateCase,
  addNewClientProperty,
  directorRelationships,
  isEmploymentCurrent,
  loadExistingClientProperties,
  removeClientsRequirementsOnTheCase,
  updatePropertyRegisteredOwners,
} from './Client.helpers'

const ClientResolver: Resolvers = {
  Deposit: {
    donorsCp: async ({ donors }) => {
      if (!donors || donors.length === 0) return []

      const donorClients = await ClientLoaderUser.loadMany(donors as unknown as string[])
      const filteredClients = donorClients.filter((client) => client !== null) as ClientVersion[]
      return filteredClients
    },
    donors: async (_parent) => {
      if (_parent.donors && _parent.donors.length > 0) {
        return (await fetchClients({ client_ids: _parent.donors as unknown as string[] })) || null
      }
      return []
    },
  },
  Query: {
    clientList: async (_parent, variables) => {
      const response = await fetchClientsWithPagination({
        ...(variables as CdmGetClientRequest),
        page_size: variables.page_size ?? undefined,
      })

      return response
    },
    client: async (_parent, { id, version }) => {
      let client: ClientVersion

      if (version === undefined || version === null) {
        client = await fetchClient({ client_ids: [id] })
      } else {
        client = await fetchClientByVersion({ client_id: id, version })
      }

      return client
    },
    clientVersion: async (_parent, { id, version, show_history }) =>
      await getClientByVersion(id, version, show_history),
    clientProtectedFields: async (_parent, { id, protectedFields }) => {
      const client = await fetchProtectedClientFields(id, protectedFields)

      return client || null
    },
    clients: async (_parent, params) => {
      const clients = await fetchClients({
        client_ids: params.ids || undefined,
        filter_organisation_id: params.organisationId || undefined,
      })

      return clients || null
    },
    clientVerifications: async (_parent, { id, show_pass, useClientApi }) =>
      await fetchClientVerifications(id, show_pass, useClientApi),
    clientOrganisationRelationship: async (_parent, { client_id, organisation_id_or_ext }) => {
      const { client_organisation_relationship } = await fetchClientOrganisationRelationships(
        client_id,
        organisation_id_or_ext,
      )
      return client_organisation_relationship || {}
    },
    clientOrganisationRelationships: async (_parent, { client_id }) => {
      const { client_organisation_relationship } = await fetchClientOrganisationRelationships(client_id)
      return (
        client_organisation_relationship && client_organisation_relationship.marketing_preferences
          ? client_organisation_relationship.marketing_preferences
          : []
      ) as MarketingPreferences[]
    },
    clientUser: async () => {
      const response = await fetchClientUser()

      return response?.client?.client_id ? formatClientUser(response?.client) : null
    },
    clientsUser: async (_, { ids }) => {
      if (!ids || ids.length === 0) return []
      const clients = (await ClientLoaderUser.loadMany(ids))?.filter((client) => client !== null) as ClientVersion[]
      return clients || []
    },
    clientsUserDetails: async (_parent, params) => {
      if (!params.ids) {
        return []
      }

      const caseClients = await ClientLoaderUser.loadMany(params.ids)
      const cleanCaseClients = caseClients.filter((client) => client !== null) as ClientVersion[]

      const director_ids = getDirectors(cleanCaseClients)

      const clientsUsers = await ClientLoaderUser.loadMany([...params.ids, ...director_ids])

      const filteredClients = clientsUsers.filter((client) => client !== null) as ClientVersion[]

      return filteredClients
    },
    clientUserProtectedFields: async (_parent, { id, protectedFields }) => {
      const client = await fetchProtectedClientUserFields(id, protectedFields)
      if (!client) return null
      return client
    },
    getPayments: async () => {
      const payments = await getPayments()
      if (!payments) return {} as GetPaymentsResponse
      return payments
    },
    getNeedsMandate: async () => {
      const needsMandate = await getNeedsMandate()
      if (!needsMandate) return {} as GetNeedsMandateResponse
      return needsMandate
    },
  },
  Mutation: {
    addClient: (_parent, { input }) => addClient(input),
    changeRelationshipType: async (_parent, { relatedClientId, relationshipType, clientId, caseId }) => {
      const client = await fetchClient({ client_ids: [clientId] })

      if (!client) {
        return null
      }

      const { remainingRelationships, relevantPersonRelationship } = separateRelationships(
        relatedClientId,
        client.details.relationships,
      )

      // IF a relationship for this client already exists
      if (relevantPersonRelationship) {
        if (
          directorRelationships.includes(relationshipType) ||
          (relationshipType === 'INVALID_RELATIONSHIP_TYPE' && !caseId)
        ) {
          await updateClient(clientId, {
            relationships: [
              ...remainingRelationships,
              { ...relevantPersonRelationship, relationship_type: relationshipType },
            ],
          })
        } else if (relationshipType === 'INVALID_RELATIONSHIP_TYPE' && caseId) {
          await addClientAndUpdateCase(relatedClientId, caseId)

          // Remove the existing relationship
          await updateClient(clientId, {
            relationships: remainingRelationships,
          })
        }
        // When no caseId passed, it means that we are changing a relationship from the client's overview screen
        // in which case it should be impossible to remove the "regular" client from case as there is no case context
      } else if (caseId) {
        const fetchedCase = await fetchCase(caseId)
        await updateCase(
          {
            client_ids: fetchedCase.details.client_ids!.filter((id) => id !== relatedClientId),
          },
          caseId,
        )
        await updateClient(clientId, {
          relationships: [
            ...remainingRelationships,
            {
              relationship_data_access_permitted: true,
              relationship_reference: relatedClientId,
              relationship_type: relationshipType,
            },
          ],
        })
      }
      return relatedClientId
    },
    addClientAndUpdateCase: async (_parent, { newClientId, caseId }) => {
      return await addClientAndUpdateCase(newClientId, caseId)
    },
    // TODO FRON-3143 Move deleteClientAndUpdateCase resolver fn to Case resolvers section
    deleteClientAndUpdateCase: async (_parent, { deleteClientId, caseId }) => {
      const fetchedCase = await fetchCase(caseId)
      if (!fetchedCase?.details?.client_ids) return fetchedCase
      const caseClients = await fetchClients({ client_ids: fetchedCase.details.client_ids })

      // Delete client ids from mortgages on this case
      const caseMortgages = await fetchMortgages({
        filter_case_id: caseId,
      })

      const selectedAndProposedMortgageStatuses: MortgageStatus[] = [
        'STATUS_SELECTED',
        'STATUS_PROPOSED',
        'STATUS_LENDER_PROPOSED',
      ]

      const selectedAndProposedMortgages = caseMortgages?.filter(
        (mortgage) => mortgage && mortgage.status && selectedAndProposedMortgageStatuses.includes(mortgage.status),
      )

      if (selectedAndProposedMortgages) {
        // Process mortgage updates sequentially to avoid backend conflicts
        for (const mortgageDetails of selectedAndProposedMortgages) {
          if (!mortgageDetails) {
            continue
          }
          await updateMortgage(mortgageDetails.mortgage_id, {
            client_ids: mortgageDetails.client_ids?.filter((clientId) => clientId !== deleteClientId),
          })
        }
      }

      const nonNaturalClient = caseClients?.find((client) => client && isNonNaturalClient(client.details))

      const { remainingRelationships, relevantPersonRelationship } = separateRelationships(
        deleteClientId,
        nonNaturalClient?.details.relationships,
      )
      const isClientOnCase = caseClients?.some((client) => client?.details.id === deleteClientId)
      const isClientNonNatural = deleteClientId === nonNaturalClient?.id

      // If client is stored directly on case, then remove the client directly from case
      if (isClientOnCase) {
        // we should remove the requirements, existing protection products and protection producst(benefits) from the case if we removing the client
        // basically everything client is associated to protection wise
        const variables = { filterCaseIds: [caseId], protectionProductDetails: true }
        // get the protection products on the case
        const protectionProducts = await fetchAllProtectionProducts(variables as CdmGetProtectionProductRequest)

        //filter the protection products that have the clients id
        const protectionProductsIdsWithDeletedClient = protectionProducts?.protection_products
          ?.filter((product) => product.details?.client_ids?.includes(deleteClientId))
          .map((product) => product.protection_id)
        // if there are any, remove them sequentially to avoid backend conflicts
        if (protectionProductsIdsWithDeletedClient) {
          for (const protection_id of protectionProductsIdsWithDeletedClient) {
            if (protection_id) {
              await deleteCaseProtectionProduct(protection_id, caseId)
            }
          }
        }
        //filter the requirements out of the fetchedCase and update the case
        const requirements = fetchedCase.details.protection?.requirements
        if (requirements) {
          const filteredRequirements = removeClientsRequirementsOnTheCase(requirements, deleteClientId)
          await updateCase(
            {
              protection: { ...fetchedCase.details.protection, requirements: filteredRequirements },
            },
            caseId,
          )
        }

        // If removing the non natural client, we need to make mark the case as not done through company and move
        // all regular clients on case and the directors of the non-natural client directly onto the case
        const naturalClients = fetchedCase.details.client_ids.filter((clientId: string) => deleteClientId !== clientId)
        const nonNaturalClients = (nonNaturalClient?.details.relationships || []).reduce<string[]>(
          (acc, relationship) => {
            if (relationship.relationship_reference) {
              acc.push(relationship.relationship_reference)
            }
            return acc
          },
          [],
        )

        const clientsFullList = uniq([...naturalClients, ...nonNaturalClients])

        if (isClientNonNatural) {
          await updateCase(
            {
              btl_through_company: false,
              client_ids: clientsFullList,
            },
            caseId,
          )
        } else if (isClientOnCase) {
          await updateCase(
            {
              client_ids: fetchedCase.details.client_ids.filter((clientId: string) => deleteClientId !== clientId),
            },
            caseId,
          )
        }
      }

      // If client shown on case as a result of a relationship to a non-natural client
      // then remove that relationship
      if (
        relevantPersonRelationship &&
        directorRelationships.includes(relevantPersonRelationship.relationship_type as ClientRelationship)
      ) {
        await updateClient(nonNaturalClient!.id, { relationships: remainingRelationships })
      }
      clearCaseFromCache(caseId)
      return await fetchCase(caseId)
    },
    updateClient: async (_, { id, input }) => {
      const result = await updateClient(id, input)
      return result
    },
    updateClientAddresses: async (_, { id: client_id, input, client_ids, property_id, useClientApi }) => {
      const filteredInput = input.map((address) => omit(address, 'property_location'))
      const response = await updateClientAddresses(client_id, filteredInput, useClientApi)

      if (!response) return

      // Retrieve existing properties for all clients
      const existingClientProperties = await loadExistingClientProperties(client_id, client_ids)

      // Map over the updated addresses, checking if the address already exists in the existing properties
      // If it doesn't exist, create a new property
      // If it does exist, update the existing property
      // If the address is current correspondence and owner occupier, add the client as a registered owner
      // If the address is not current correspondence or owner occupier, remove the client from registered owners
      // Update the property with the new address fields to keep properties and addresses in sync
      input.forEach(async (updatedAddress) => {
        // Try to find an existing property for this address
        let existingProperty: Maybe<PropertyVersion> = null
        const { is_current_correspondence, residential_status } = updatedAddress

        if (!isEmpty(existingClientProperties)) {
          existingProperty = existingClientProperties!.find((property) => {
            return property.details?.address?.postcode === updatedAddress.address.postcode
          })
        }

        const isOwnerOccupier = residential_status === 'OWNER_OCCUPIER'

        if (!existingProperty) {
          // If no property exists and address is current correspondence and owner occupier, add new property
          if (is_current_correspondence && isOwnerOccupier) {
            await addNewClientProperty(client_id, updatedAddress, property_id, useClientApi)
            ClientPropertyLoader.clear(client_id)
          }
        } else {
          const propertyId = existingProperty?.id
          const registeredOwners = existingProperty?.details?.registered_owners_details || []
          const isClientRegisteredOwner = registeredOwners.includes(client_id)

          // Case 1: Current correspondence address with owner occupier status
          // Add client(s) as registered owners if not already present
          if (is_current_correspondence && isOwnerOccupier && propertyId) {
            const clientsToBeAddedAsOwners = client_ids?.filter((currentId) => !registeredOwners.includes(currentId))

            if (!isEmpty(clientsToBeAddedAsOwners)) {
              await updatePropertyRegisteredOwners(propertyId, clientsToBeAddedAsOwners!)
            }
          }
          // Case 2: Client changes status from owner to tenant but other owners remain
          // Remove client from registered owners, or clear owners if none remain
          else if (!isOwnerOccupier && isClientRegisteredOwner && propertyId) {
            const updatedOwners = registeredOwners.filter((ownerId: string) => ownerId !== client_id)

            if (updatedOwners.length === 0) {
              // No owners remain, clear registered owners
              await updateProperty({ registered_owners_details: [] }, propertyId, useClientApi)
              ClientPropertyLoader.clear(client_id)
            } else {
              // Remove client from owners
              await updateProperty({ registered_owners_details: updatedOwners }, propertyId)
              ClientPropertyLoader.clearAll()
            }
          }
          // Case 3: Non-owner status (tenant or living with family)
          // Remove client from property if they are a registered owner
          else if (is_current_correspondence && !isOwnerOccupier && !isNil(propertyId) && isClientRegisteredOwner) {
            await updateProperty({ registered_owners_details: [] }, propertyId, useClientApi)
            ClientPropertyLoader.clear(client_id)
          }
        }
      })

      // Map over the other clients and sync the address fields ONLY (not other fields such as ownership or move dates as these are unique to each client)
      const otherClientIds = client_ids?.filter((clientId) => clientId !== client_id)

      otherClientIds?.forEach(async (otherClientId) => {
        const otherClient = await fetchClient({ client_ids: [otherClientId] })
        if (!otherClient) return
        const otherClientAddresses =
          otherClient.details.addresses?.map((address) => omit(address, 'property_location')) ?? []

        const updatedOtherClientAddresses = otherClientAddresses.map((existingAddress) => {
          const updatedAddressFields = input.find((updatedAddress) => updatedAddress.id === existingAddress.id)?.address
          return updatedAddressFields ? { ...existingAddress, address: updatedAddressFields } : existingAddress
        })

        await updateClient(otherClientId, { addresses: updatedOtherClientAddresses })
      })

      return response
    },
    updateClientPreviousNames: async (_, { id, input }) => {
      // To stop from sending obfuscated data to backend,
      // we filter out previous_name with an *
      if (input?.previous_names) {
        input?.previous_names?.forEach((name, index, currentArray) => {
          if (name?.previous_first_name && name.previous_first_name.includes('**')) {
            delete currentArray[index]?.previous_first_name
          }
          if (name?.previous_surname && name.previous_surname.includes('**')) {
            delete currentArray[index]?.previous_surname
          }
        })
      }

      const result = updateClient(id, {
        previous_names: input.previous_names,
      })

      return result
    },
    runIdVerification: (_parent, { id }) => requestEidvCheck(id),
    updateClientIncome: (_, { id, input }) => updateClient(id, { income_and_employment: input }),
    updateClientAccounts: (_parent, { id, input }) => updateClient(id, { accounts: input }),
    updateClientDeposits: (_parent, { id, input }) => {
      const client = updateClient(id, { deposits: input })
      return client
    },
    updateClientDebts: (_parent, { id, input }) => updateClient(id, { debts: input }),
    updateClients: async (_, payload) => {
      const clients = await Promise.all(
        payload.clients.map((client: ClientInput) => {
          const { id, ...details } = client

          if (!id) {
            throw new GraphqlException('Cannot complete updateClients mutation without client id')
          }

          return updateClient(id, details)
        }),
      )

      return clients.filter(Boolean) as ClientVersion[]
    },
    updateClientCcjs: (
      _parent,
      { id, input: { credit_history_adverse_details_has_client_had_ccj, credit_history_county_court_judgements } },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_client_had_ccj,
        credit_history_county_court_judgements,
      }),
    updateClientCreditRefusals: (
      _parent,
      {
        id,
        input: { credit_history_have_you_ever_had_an_application_denied_or_refused, mortgage_application_refused },
      },
    ) =>
      updateClient(id, {
        credit_history_have_you_ever_had_an_application_denied_or_refused,
        mortgage_application_refused,
      }),
    updateClientBankruptcyEvents: (
      _parent,
      {
        id,
        input: { credit_history_adverse_details_has_client_declared_bankruptcy, credit_history_bankruptcy_events },
      },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_client_declared_bankruptcy,
        credit_history_bankruptcy_events,
      }),
    updateClientDefaultEvents: (
      _parent,
      { id, input: { credit_history_adverse_details_has_client_had_default, credit_history_default_events } },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_client_had_default,
        credit_history_default_events,
      }),
    updateClientDebtManagementPlans: (
      _parent,
      {
        id,
        input: {
          credit_history_adverse_details_has_client_had_debt_management_plan,
          credit_history_debt_management_plans,
        },
      },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_client_had_debt_management_plan,
        credit_history_debt_management_plans,
      }),
    updateClientArrears: (
      _parent,
      { id, input: { credit_history_adverse_details_has_arrears, credit_history_arrears } },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_arrears,
        credit_history_arrears,
      }),
    updateClientIvas: (
      _parent,
      {
        id,
        input: { credit_history_adverse_details_has_client_had_iva, credit_history_individual_voluntary_arrangements },
      },
    ) =>
      updateClient(id, {
        credit_history_adverse_details_has_client_had_iva,
        credit_history_individual_voluntary_arrangements,
      }),
    updateClientOrganisationRelationship: async (_parent, { client_id, organisation_id_or_ext, input }) => {
      const { client_organisation_relationship } = await updateClientOrganisationRelationship(
        client_id,
        organisation_id_or_ext,
        input,
      )
      return (
        client_organisation_relationship &&
        client_organisation_relationship.marketing_preferences &&
        client_organisation_relationship.marketing_preferences
          ? client_organisation_relationship.marketing_preferences
          : []
      ) as MarketingPreferences[]
    },
    createClientOrganisationRelationship: async (_parent, { client_id, organisation_id_or_ext, input }) => {
      const { client_organisation_relationship } = await createClientOrganisationRelationship(
        client_id,
        organisation_id_or_ext,
        input,
      )
      return {
        client_id: client_organisation_relationship?.client_id,
        org_id: client_organisation_relationship?.org_id,
        relationship_type: client_organisation_relationship?.relationship_type,
      }
    },

    //Client Portal
    addClientCp: async (_, { input }) => addClient(input),
    updateClientUser: async (_, { id, input }) => {
      const formattedInput = formatClientAsRequestBody(input)

      // By default spread nothing onto payload
      let hasAddressesToFormat = {}
      let hasNationalitiesToFormat = {}
      let hasCountryOfBirthToFormat = {}

      if (formattedInput.addresses && formattedInput.addresses.length > 0) {
        hasAddressesToFormat = {
          addresses: formattedInput.addresses,
        }
      }

      if (formattedInput.nationalities && formattedInput.nationalities.length > 0) {
        hasNationalitiesToFormat = {
          nationalities: formatNationalities(formattedInput.nationalities),
        }
      }

      const response = await updateClientUser(id, {
        ...formattedInput,
        ...hasAddressesToFormat,
        ...hasNationalitiesToFormat,
        ...hasCountryOfBirthToFormat,
      })
      ClientLoaderUser.clearAll()

      return formatClientUser(response.client as CdmClient)
    },
    upsertCompany: (_parent, { company, user_id, organisation_id }) => upsertCompany(company, user_id, organisation_id),
    upsertCompanyAndDirectors: (_parent, { company, user_id, organisation_id }) =>
      upsertCompanyAndDirectors(company, user_id, organisation_id),
    createMandate: async () => await createMandate(),
    getRiskReport: async (_parent, { input }) => {
      const response = await getRiskReport(input)

      return {
        __typename: 'ClientVersion',
        id: input.client_id,
        version: input.client_version,
        details: {
          __typename: 'Client',
          id: input.client_id,
          risk_reports: [
            {
              ...response,
              __typename: 'RiskReport',
            },
          ],
        },
      }
    },
  },
  Client: {
    cases: async (_parent) => (_parent.client_id ? await fetchCasesForClient(_parent.client_id) : null),
    debts: async (parent) => parent.debts || [],

    miDocuments: async ({ client_id }, { filters }) => {
      return client_id ? await getMiDocumentSummaries('client', client_id, filters) : null
    },
    document_totals: async ({ id }, { filters }) => {
      const documentTotals = await getMiDocumentTotals('client', id, filters)

      return documentTotals as MiDocumentTotal[]
    },
    properties: async ({ client_id }, { includeDirectorsProperties = false }) => {
      if (!client_id) return null

      const properties = (await fetchClientProperties(client_id)) ?? []

      if (!includeDirectorsProperties) return properties

      // Fetch director properties
      const caseClients = await fetchClients({ client_ids: [client_id] })
      const directorIds = getDirectors(caseClients) ?? []

      if (directorIds.length === 0) return properties

      const clientProperties: PropertyVersion[] =
        ((await fetchPropertiesForClients({
          clientIds: directorIds,
          queryParams: { property_details: true },
          fetchAllPropertiesForClient: true,
        })) as PropertyVersion[]) || []

      const propertiesWithDirectors = [...properties, ...clientProperties]
      // Remove duplicates
      return [...new Map(propertiesWithDirectors.map((item) => [item.id, item])).values()]
    },
    mortgages: async ({ client_id }) => {
      if (!client_id) {
        return null
      }

      const mortgages = await fetchMortgages({ filter_client_ids: [client_id] })
      const formattedMortgages = mortgages
        ? (mortgages.filter(
            (mortgage) =>
              mortgage !== null &&
              (mortgage.status === 'STATUS_CREDIT_REPORT_CURRENT' ||
                mortgage.status === 'STATUS_CREDIT_REPORT_SETTLED'),
          ) as Mortgage[])
        : []

      return formattedMortgages
    },
    relatedClients: async ({ relationships }, { relationshipTypes, fromClientPortal }) => {
      if (!relationships || relationships.length === 0) return []

      const clientIds = relationships.reduce((acc, relationship) => {
        // if relationshipTypes is not specified get all related clients
        const isRelevantRelationshipType =
          !relationshipTypes?.length ||
          relationshipTypes.includes(relationship.relationship_type || 'INVALID_RELATIONSHIP_TYPE')

        if (relationship.relationship_reference && isRelevantRelationshipType) {
          acc.push(relationship.relationship_reference)
        }

        return acc
      }, [] as string[])

      if (clientIds.length === 0) return []

      if (fromClientPortal) {
        // only fetch basic info as otherwise client might not have access the the related client's data
        return fetchClientsBatchFunction({ client_ids: clientIds, show_basic_info: true })
      }

      const clients = await fetchClients({ client_ids: clientIds })
      return clients || null
    },

    // Client Portal
    clientUserIdd: async ({ client_id }) => {
      const { documents } = await getMiDocumentSummaries('client', client_id || '', { include_archived: false }, true)

      const iddDocuments = documents?.filter((document) => {
        return document.template_type === 'CLIENT_PORTAL_IDD'
      })

      // Fetch document details of CP and CR Idds
      if (iddDocuments && iddDocuments.length > 0) {
        let iddDocumentsDetails = []
        for (const iddDocument of iddDocuments) {
          const documentDetails = await getClientUserDocumentDetails(iddDocument.document_id!, {
            document_details: true,
          })
          if (documentDetails?.document_id) {
            iddDocumentsDetails.push(documentDetails)
          }
        }

        // Filter out Credit Report Idds
        const clientPortalIdds = iddDocumentsDetails.filter(
          (doc) => !doc?.rendered_organisation_ids?.includes(config.ACRE_SUPPORT_ORG_ID),
        )

        // Get latest Client Portal Idd
        const latestClientPortalIdd = clientPortalIdds?.sort((a, b) =>
          a?.date_stored && b?.date_stored ? Date.parse(b.date_stored) - Date.parse(a.date_stored) : 0,
        )[0]

        // // Check if Client has already verified Client Poral Idd
        const docVerifications = await getDocumentVerificationCp(latestClientPortalIdd?.document_id!, {
          verification_details: true,
        })

        if (docVerifications?.verifications && client_id) {
          const clientVerification = docVerifications?.verifications.filter(
            (verification) => verification.client_id === client_id,
          )

          return !isEmpty(clientVerification) ? {} : latestClientPortalIdd
        }
        // Return latest Client Portal Idd if not yet signed
        return latestClientPortalIdd
      }
      return null
    },
    propertiesCp: async ({ client_id }) => {
      return client_id ? await ClientPropertyLoaderCp.load(client_id) : null
    },
  },
  ClientIncome: {
    is_current: async (_parent) => (_parent ? isEmploymentCurrent(_parent) : null),
  },
}

export default ClientResolver
